import type { DocumentBlock } from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import {
    CONTENT_HEADER_RE,
    FOR_HEADER_RE,
    IMPORT_RE,
    parseDefineHeader,
    USE_HEADER_RE,
} from "@magic-md/core/patterns";
import {
    ChevronDown,
    ChevronRight,
    ChevronsDownUp,
    ChevronsUpDown,
    FileInput,
    FileText,
    Hash,
    Link2,
    List,
    Puzzle,
    Repeat,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import {
    type ExecutionStatus,
    usePluginExecutionStore,
} from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { SidebarCloseButton } from "./SidebarCloseButton";

/* ── Types ── */
export interface TocHeading {
    /** 1-based line number in the source content (editor mode) */
    line: number;
    /** Heading level: 1–6 */
    level: number;
    /** Heading text (without `#` prefix) */
    text: string;
    /** Entry kind: heading or plugin define/render/for/import/use/content */
    kind: "heading" | "define" | "render" | "for" | "import" | "use" | "content";
    /** Plugin name (only for define entries) */
    pluginName?: string;
    /** Definition ID (for define/render/for entries, used for status lookup) */
    definitionId?: string;
    /** Stable id for scrolling (preview mode) */
    tocId?: string;
}

/**
 * Extract headings and ::define blocks from raw markdown content.
 * ATX headings (lines starting with `# `) and ::define[id]{plugin="..."}
 * are recognized. Define blocks get level = previous heading level + 1.
 * Code blocks (fenced with ```) are skipped.
 */
function extractHeadings(content: string): TocHeading[] {
    const headings: TocHeading[] = [];
    const lines = content.split("\n");
    let inCodeBlock = false;
    let lastHeadingLevel = 0;
    // Track for-block nesting — stack-based so nested for-blocks restore correctly
    let forNestingDepth = 0;
    const forBaseLevelStack: number[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trimStart();

        // Toggle code block state
        if (trimmed.startsWith("```")) {
            inCodeBlock = !inCodeBlock;
            continue;
        }
        if (inCodeBlock) continue;

        // Track ::endfor
        if (trimmed === "::endfor") {
            if (forNestingDepth > 0) {
                forNestingDepth--;
                forBaseLevelStack.pop();
            }
            continue;
        }

        // Match ATX heading: # Heading
        const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            const rawLevel = headingMatch[1].length;
            const forBaseLevel =
                forBaseLevelStack.length > 0
                    ? forBaseLevelStack[forBaseLevelStack.length - 1]
                    : 0;
            // Inside a for-block → ensure level > forBaseLevel so it nests under the for entry
            const level =
                forNestingDepth > 0
                    ? Math.min(Math.max(rawLevel + forNestingDepth, forBaseLevel + 1), 6)
                    : rawLevel;
            lastHeadingLevel = level;
            headings.push({
                line: i + 1, // 1-based
                level,
                text: headingMatch[2].replace(/\s*#+\s*$/, "").trim(),
                kind: "heading",
            });
            continue;
        }

        // Match ::define[id]{plugin="name"}
        const defineInfo = parseDefineHeader(trimmed);
        if (defineInfo) {
            const forBaseLevel =
                forBaseLevelStack.length > 0
                    ? forBaseLevelStack[forBaseLevelStack.length - 1]
                    : 0;
            const baseLevel =
                forNestingDepth > 0
                    ? Math.min(forBaseLevel + 1, 6)
                    : Math.min(lastHeadingLevel + 1, 6);
            headings.push({
                line: i + 1,
                level: baseLevel,
                text: defineInfo.id,
                kind: "define",
                pluginName: defineInfo.pluginName,
                definitionId: defineInfo.id,
            });
            continue;
        }

        // Match ::for[id]{...}
        const forMatch = trimmed.match(FOR_HEADER_RE);
        if (forMatch) {
            const forLevel = Math.min(lastHeadingLevel + 1, 6);
            headings.push({
                line: i + 1,
                level: forLevel,
                text: forMatch[1],
                kind: "for",
                definitionId: forMatch[1],
            });
            forNestingDepth++;
            forBaseLevelStack.push(forLevel);
            continue;
        }

        // Match ::import{from="..." ns="..."}
        const importMatch = trimmed.match(IMPORT_RE);
        if (importMatch) {
            const attrs = importMatch[1];
            const fromMatch = attrs.match(/from="([^"]+)"/);
            const nsMatch = attrs.match(/ns="([^"]+)"/);
            const ns = nsMatch?.[1];
            const displayText = ns ?? fromMatch?.[1] ?? "import";
            const baseLevel =
                forNestingDepth > 0
                    ? Math.min(
                        (forBaseLevelStack[forBaseLevelStack.length - 1] ?? 0) + 1,
                        6,
                    )
                    : Math.min(lastHeadingLevel + 1, 6);
            headings.push({
                line: i + 1,
                level: baseLevel,
                text: displayText,
                kind: "import",
                definitionId: ns,
            });
            continue;
        }

        // Match ::use[id]{from="..." ...}
        const useMatch = trimmed.match(USE_HEADER_RE);
        if (useMatch) {
            const baseLevel =
                forNestingDepth > 0
                    ? Math.min(
                        (forBaseLevelStack[forBaseLevelStack.length - 1] ?? 0) + 1,
                        6,
                    )
                    : Math.min(lastHeadingLevel + 1, 6);
            headings.push({
                line: i + 1,
                level: baseLevel,
                text: useMatch[1],
                kind: "use",
                definitionId: useMatch[1],
            });
            continue;
        }

        // Match ::content[id]{...}
        const contentMatch = trimmed.match(CONTENT_HEADER_RE);
        if (contentMatch) {
            const baseLevel =
                forNestingDepth > 0
                    ? Math.min(
                        (forBaseLevelStack[forBaseLevelStack.length - 1] ?? 0) + 1,
                        6,
                    )
                    : Math.min(lastHeadingLevel + 1, 6);
            headings.push({
                line: i + 1,
                level: baseLevel,
                text: contentMatch[1],
                kind: "content",
                definitionId: contentMatch[1],
            });
        }
    }

    return headings;
}

/**
 * Extract headings and ::render blocks from parsed document blocks.
 * Used in preview-only mode where define blocks are hidden and
 * render blocks are the visible plugin elements.
 */
function extractPreviewHeadings(blocks: DocumentBlock[]): TocHeading[] {
    const headings: TocHeading[] = [];
    let lastHeadingLevel = 0;

    function processBlocks(
        blockList: DocumentBlock[],
        levelOffset: number,
        minLevel: number,
    ) {
        for (let blockIndex = 0; blockIndex < blockList.length; blockIndex++) {
            const block = blockList[blockIndex];

            if (block.type === "markdown") {
                // Extract headings from markdown content (skip code fences)
                let headingInBlock = 0;
                let inCodeBlock = false;
                const lines = block.content.split("\n");
                for (const line of lines) {
                    const trimmed = line.trimStart();
                    if (trimmed.startsWith("```")) {
                        inCodeBlock = !inCodeBlock;
                        continue;
                    }
                    if (inCodeBlock) continue;
                    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
                    if (headingMatch) {
                        const rawLevel = headingMatch[1].length;
                        const level = Math.min(
                            Math.max(rawLevel + levelOffset, minLevel),
                            6,
                        );
                        lastHeadingLevel = level;
                        headings.push({
                            line: blockIndex,
                            level,
                            text: headingMatch[2].replace(/\s*#+\s*$/, "").trim(),
                            kind: "heading",
                            tocId: `toc-h-${blockIndex}-${headingInBlock}`,
                        });
                        headingInBlock++;
                    }
                }
            } else if (
                block.type === "block-render" ||
                block.type === "inline-render"
            ) {
                headings.push({
                    line: blockIndex,
                    level: Math.min(lastHeadingLevel + 1, 6),
                    text: block.definitionId,
                    kind: "render",
                    definitionId: block.definitionId,
                    tocId: `toc-render-${block.definitionId}`,
                });
            } else if (block.type === "for-block") {
                const forLevel = Math.min(lastHeadingLevel + 1, 6);
                headings.push({
                    line: blockIndex,
                    level: forLevel,
                    text: block.id,
                    kind: "for",
                    definitionId: block.id,
                    tocId: `toc-render-${block.id}`,
                });
                // Process for-block children — ensure all nested at least forLevel+1
                if (block.children && block.children.length > 0) {
                    const savedLevel = lastHeadingLevel;
                    lastHeadingLevel = forLevel;
                    processBlocks(block.children, levelOffset + 1, forLevel + 1);
                    lastHeadingLevel = savedLevel;
                }
            }
            // definitions are hidden in preview, skip them
        }
    }

    processBlocks(blocks, 0, 0);

    return headings;
}

/* ── Status dot component ── */
function StatusDot({ status }: { status: ExecutionStatus }) {
    const t = useT("editor");
    if (status === "idle") return null;
    return (
        <span
            className={cn(
                "w-1.5 h-1.5 rounded-full shrink-0 ml-auto",
                "theme-accent-fill",
                status === "running" && "animate-pulse",
            )}
            style={getThemeAccentStyle(
                status === "running"
                    ? "running"
                    : status === "success"
                        ? "success"
                        : "error",
            )}
            title={
                status === "running"
                    ? t("status.running")
                    : status === "success"
                        ? t("status.success")
                        : t("status.error")
            }
        />
    );
}

function TocStatusDot({
    kind,
    definitionId,
}: {
    kind: TocHeading["kind"];
    definitionId: string;
}) {
    const status = usePluginExecutionStore((state) => {
        switch (kind) {
            case "for":
                return state.forExecutions.get(definitionId)?.status ?? "idle";
            case "content":
                return state.contentExecutions.get(definitionId)?.status ?? "idle";
            case "use":
                return state.useExecutions.get(definitionId)?.status ?? "idle";
            case "import":
                return state.importExecutions.get(definitionId)?.status ?? "idle";
            default:
                return state.executions.get(definitionId)?.status ?? "idle";
        }
    });

    return <StatusDot status={status as ExecutionStatus} />;
}

/* ── Collapse helpers ── */

/** Check whether a heading at `index` has children (next item has a deeper level). */
function hasChildren(headings: TocHeading[], index: number): boolean {
    if (index + 1 >= headings.length) return false;
    return headings[index + 1].level > headings[index].level;
}

/**
 * Compute the set of heading indices that are visible given collapsed set.
 * A heading is hidden when any ancestor heading is collapsed.
 */
function computeVisibleIndices(
    headings: TocHeading[],
    collapsed: Set<number>,
): Set<number> {
    const visible = new Set<number>();
    // Stack of (level, isCollapsed) representing ancestor state
    // We track the deepest collapsed ancestor level
    let hiddenBelowLevel: number | null = null;

    for (let i = 0; i < headings.length; i++) {
        const h = headings[i];

        // If we moved back to a level <= the collapsed ancestor, we're out of the hidden zone
        if (hiddenBelowLevel !== null && h.level <= hiddenBelowLevel) {
            hiddenBelowLevel = null;
        }

        if (hiddenBelowLevel !== null) {
            // This heading is hidden under a collapsed parent
            continue;
        }

        visible.add(i);

        // If this heading is collapsed, hide everything deeper
        if (collapsed.has(i)) {
            hiddenBelowLevel = h.level;
        }
    }

    return visible;
}

/** Get all indices that have children (collapsible). */
function getCollapsibleIndices(headings: TocHeading[]): Set<number> {
    const result = new Set<number>();
    for (let i = 0; i < headings.length; i++) {
        if (hasChildren(headings, i)) {
            result.add(i);
        }
    }
    return result;
}

/* ── TOC Panel ── */
export function TocPanel() {
    const t = useT("editor");
    const activeTab = useEditorStore((s) =>
        s.tabs.find((t) => t.id === s.activeTabId),
    );
    const cursorLine = useEditorStore((s) => s.cursorLine);
    const viewMode = useEditorStore((s) => s.viewMode);
    const parsedDocument = useEditorStore((s) => s.parsedDocument);

    const isPreviewOnly = viewMode === "preview";

    const headings = useMemo(() => {
        if (isPreviewOnly && parsedDocument) {
            return extractPreviewHeadings(parsedDocument.blocks);
        }
        return extractHeadings(activeTab?.content ?? "");
    }, [isPreviewOnly, parsedDocument, activeTab?.content]);

    /* ── Collapse state ── */
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

    const collapsibleIndices = useMemo(
        () => getCollapsibleIndices(headings),
        [headings],
    );

    const visibleIndices = useMemo(
        () => computeVisibleIndices(headings, collapsed),
        [headings, collapsed],
    );

    const isAllCollapsed =
        collapsibleIndices.size > 0 &&
        [...collapsibleIndices].every((i) => collapsed.has(i));

    const toggleCollapse = useCallback((index: number) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    }, []);

    const collapseAll = useCallback(() => {
        setCollapsed(new Set(collapsibleIndices));
    }, [collapsibleIndices]);

    const expandAll = useCallback(() => {
        setCollapsed(new Set());
    }, []);

    /** Find which heading the cursor is currently inside (editor mode) */
    const activeHeadingLine = useMemo(() => {
        if (isPreviewOnly) return null;
        let closest: number | null = null;
        for (const h of headings) {
            if (h.line <= cursorLine) {
                closest = h.line;
            } else {
                break;
            }
        }
        return closest;
    }, [headings, cursorLine, isPreviewOnly]);

    const handleClick = (heading: TocHeading) => {
        if (isPreviewOnly && heading.tocId) {
            // In preview mode, scroll the preview panel to the target element
            const el = document.querySelector(`[data-toc-id="${heading.tocId}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                // Subtle flash animation to indicate the target
                el.classList.remove("toc-flash");
                // Force reflow so re-adding the class restarts the animation
                void (el as HTMLElement).offsetWidth;
                el.classList.add("toc-flash");
            }
        } else if (heading.tocId && viewMode === "split") {
            // In split mode, also flash the preview element if visible
            const el = document.querySelector(`[data-toc-id="${heading.tocId}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.remove("toc-flash");
                void (el as HTMLElement).offsetWidth;
                el.classList.add("toc-flash");
            }
            // And scroll the editor
            window.dispatchEvent(
                new CustomEvent("magic-md:goto-line", {
                    detail: { line: heading.line, flash: true },
                }),
            );
        } else {
            // In editor mode, scroll CodeMirror to the source line
            window.dispatchEvent(
                new CustomEvent("magic-md:goto-line", {
                    detail: { line: heading.line, flash: true },
                }),
            );
        }
    };

    const minLevel =
        headings.length > 0 ? Math.min(...headings.map((h) => h.level)) : 1;

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("sidebar.toc")}
                </span>
                <div className="flex items-center gap-1">
                    {collapsibleIndices.size > 0 && (
                        <button
                            type="button"
                            onClick={isAllCollapsed ? expandAll : collapseAll}
                            className="flex items-center justify-center w-5 h-5 rounded text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted/60 transition-colors"
                            title={isAllCollapsed ? t("toc.expandAll") : t("toc.collapseAll")}
                        >
                            {isAllCollapsed ? (
                                <ChevronsUpDown size={13} />
                            ) : (
                                <ChevronsDownUp size={13} />
                            )}
                        </button>
                    )}
                    <SidebarCloseButton />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto p-1.5">
                {headings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                        <List size={24} className="mb-2 opacity-40" />
                        <span className="text-xs">{t("toc.empty")}</span>
                    </div>
                ) : (
                    <nav className="flex flex-col gap-px">
                        {headings.map((heading, index) => {
                            if (!visibleIndices.has(index)) return null;

                            const indent = heading.level - minLevel;
                            const isActive = heading.line === activeHeadingLine;
                            const isPlugin =
                                heading.kind === "define" || heading.kind === "render";
                            const isFor = heading.kind === "for";
                            const isImport = heading.kind === "import";
                            const isUse = heading.kind === "use";
                            const isContent = heading.kind === "content";
                            const isModule = isImport || isUse || isContent;
                            const iconAccent = isPlugin
                                ? "define"
                                : isFor
                                    ? "for"
                                    : isImport
                                        ? "import"
                                        : isUse
                                            ? "use"
                                            : isContent
                                                ? "content"
                                                : null;
                            const Icon = isFor
                                ? Repeat
                                : isImport
                                    ? FileInput
                                    : isUse
                                        ? Link2
                                        : isContent
                                            ? FileText
                                            : isPlugin
                                                ? Puzzle
                                                : Hash;
                            const isCollapsible = collapsibleIndices.has(index);
                            const isCollapsed = collapsed.has(index);

                            return (
                                <div
                                    key={heading.tocId ?? `${heading.line}-${heading.text}`}
                                    className="flex items-center"
                                    style={{
                                        paddingLeft: `${Math.max(0, indent * 14)}px`,
                                    }}
                                >
                                    {/* Collapse chevron */}
                                    {isCollapsible ? (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCollapse(index);
                                            }}
                                            className="flex items-center justify-center w-4 h-4 shrink-0 rounded text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/60 transition-colors"
                                        >
                                            {isCollapsed ? (
                                                <ChevronRight size={10} />
                                            ) : (
                                                <ChevronDown size={10} />
                                            )}
                                        </button>
                                    ) : (
                                        <span className="w-4 shrink-0" />
                                    )}

                                    {/* Heading button */}
                                    <button
                                        type="button"
                                        onClick={() => handleClick(heading)}
                                        className={cn(
                                            "flex items-center gap-1.5 flex-1 min-w-0 text-left px-1.5 py-1.5 rounded text-xs transition-colors group",
                                            isActive
                                                ? "bg-primary/10 text-primary"
                                                : "text-foreground/80 hover:bg-muted/60",
                                        )}
                                        title={
                                            heading.kind === "define"
                                                ? `${heading.pluginName} · Line ${heading.line}`
                                                : heading.kind === "render"
                                                    ? heading.text
                                                    : heading.kind === "for"
                                                        ? t("toc.tooltip.for", { line: heading.line })
                                                        : heading.kind === "import"
                                                            ? t("toc.tooltip.import", { line: heading.line })
                                                            : heading.kind === "use"
                                                                ? t("toc.tooltip.use", { line: heading.line })
                                                                : heading.kind === "content"
                                                                    ? t("toc.tooltip.content", { line: heading.line })
                                                                    : t("toc.tooltip.line", { line: heading.line })
                                        }
                                    >
                                        <Icon
                                            size={11}
                                            className={cn(
                                                "shrink-0 transition-colors",
                                                isActive
                                                    ? "text-primary"
                                                    : iconAccent
                                                        ? "theme-accent-subtle"
                                                        : "text-muted-foreground/50 group-hover:text-muted-foreground",
                                            )}
                                            style={
                                                !isActive && iconAccent
                                                    ? getThemeAccentStyle(iconAccent)
                                                    : undefined
                                            }
                                        />
                                        <span
                                            className={cn(
                                                "truncate",
                                                isPlugin || isFor || isModule
                                                    ? "font-mono text-[11px]"
                                                    : heading.level <= 2 && "font-medium",
                                            )}
                                        >
                                            {heading.text}
                                        </span>
                                        {heading.kind === "define" && heading.pluginName && (
                                            <span className="shrink-0 text-[10px] text-muted-foreground/60 font-normal">
                                                {heading.pluginName}
                                            </span>
                                        )}
                                        {heading.kind === "for" && (
                                            <span className="shrink-0 text-[10px] text-muted-foreground/60 font-normal">
                                                for
                                            </span>
                                        )}
                                        {heading.kind === "import" && (
                                            <span className="shrink-0 text-[10px] text-muted-foreground/60 font-normal">
                                                import
                                            </span>
                                        )}
                                        {heading.kind === "use" && (
                                            <span className="shrink-0 text-[10px] text-muted-foreground/60 font-normal">
                                                use
                                            </span>
                                        )}
                                        {heading.kind === "content" && (
                                            <span className="shrink-0 text-[10px] text-muted-foreground/60 font-normal">
                                                content
                                            </span>
                                        )}
                                        {heading.definitionId && (
                                            <TocStatusDot
                                                kind={heading.kind}
                                                definitionId={heading.definitionId}
                                            />
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </nav>
                )}
            </div>
        </div>
    );
}
